import { Surface } from "--floponents-web/atoms/Surface";
import { useBreakpoint } from "--components/hooks/useBreakpoint";
import { IconButton } from "--components/buttons/iconButton";
import { noWrapCss } from "--components/typography";

import { scrollbarCSS } from "--floponents-web/common/scrollbarCss";
import * as React from "react"
import styled, { CSSProperties, css } from "styled-components"
import IcoBack from "@floji-internal/mui-icons/ArrowBackIos"
import { useStableHistory } from "../../../common/hooks/useStableHistory";
import { useNavigationAncestorPath } from "../../Navigation/sitemap-nav";
import { NavigationStackContext } from "../../Navigation/NavigationStackUIStatePreserver";
const HEADER_HEIGHT = 57;

export const StyledContentLayoutLeftSlot = styled.div<{ mobile: boolean }>`
    color: ${props => props.theme.palette.text.primary};
    flex: 0;
    padding-right: ${props => props.theme.spacing(props.mobile ? 1 : 2)};
`
const StyledMaybeContentLayoutLeftSlot = styled(StyledContentLayoutLeftSlot)``

const StyledContentLayoutHeader = styled.div<{ mobile: boolean, hasNav: boolean }>`
    display:flex;
    box-sizing: border-box;
    border-bottom: 1px solid ${props => props.theme.palette.ui.border};
    padding: 0px;
    background: ${props => props.theme.palette.surface};
    align-items: center;
    flex: 0 0 ${HEADER_HEIGHT}px;
    position: relative;
    padding: ${props => props.theme.spacing(0, props.mobile ? 1 : 2)};
    & ${StyledMaybeContentLayoutLeftSlot} {
        ${props => props.hasNav && css`display:none;` || ""}
    }
`;


export const StyledContentLayoutDescription = styled.div`
    display:flex;
    min-width: 0;
    overflow: hidden;
    color: ${props => props.theme.palette.text.disabled};
    font-family: ${props => props.theme.typography.body2.fontFamily};
    font-size: ${props => props.theme.typography.body2.fontSize};
    line-height:1.3;
    flex-grow:1;
    ${noWrapCss};
    width: 100%;
`

const StyledContentLayoutTitle = styled.div`
    position: relative;
    display: flex;
    gap: ${p => p.theme.spacing(0.5)};
    font-weight: ${props => props.theme.typography.bold};
    width: 100%;
    align-items: center;
    ${noWrapCss};
`
const SplitContents = styled.div`
    display:flex;
    height:100%;
    position: relative;
    flex:1;
    overflow: hidden;
`

const StyledContentLayoutContents = styled.div<{ mobile: boolean }>`
    height:100%;
    padding: 0px ${props => props.theme.spacing(props.mobile ? 1 : 2)};
    & > ${Surface} {
        margin-top: ${props => props.theme.spacing(2)};
    };
    flex-grow:1;
    flex-shrink:1;
    ${scrollbarCSS()};
`
const StyledContentLayout = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background-color: ${p => p.theme.palette.background};
`
export const ContentLayoutTitle = StyledContentLayoutTitle
export const ContentLayoutDescription = StyledContentLayoutDescription

export const StyledContentLayoutRightSlot = styled.div<{ mobile: boolean }>`
    color: ${props => props.theme.palette.text.primary};
    flex: 1 1 30%;
    justify-content: end;
    align-items: center;
    padding-left: ${props => props.theme.spacing(props.mobile ? 1 : 2)};
    display: flex;
    gap: ${p => p.theme.spacing(0.5)};
`


export const ContentLayoutMainSlot = styled.div`
    display: flex;
    overflow: hidden;
    color: ${props => props.theme.palette.text.primary};
    ${props => props.theme.typography.body1};
    margin: 0px;
    flex-direction: column;
    flex: 1 1 100%;
    position: relative;
`


export const ContentLayoutMaybeLeftSlot: React.FC = props => {
    return <StyledMaybeContentLayoutLeftSlot mobile={false}>{props.children}</StyledMaybeContentLayoutLeftSlot>
}

interface ContentLayoutRightSlotProps {
    alwaysShow?: boolean;
}
export const ContentLayoutRightSlot: React.FC<ContentLayoutRightSlotProps> = props => {
    const [bp] = useBreakpoint();
    return <StyledContentLayoutRightSlot mobile={props.alwaysShow || bp === "mobile"}>{props.children}</StyledContentLayoutRightSlot>
}

export const ContentLayoutHeader: React.FC<{ className?: string }> = props => {
    const [bp] = useBreakpoint();
    const path = useNavigationAncestorPath();
    const history = useStableHistory();
    const navigationContext = React.useContext(NavigationStackContext)
    //concealed not always working here! and probably isn't right fix anyway
    return <StyledContentLayoutHeader className={props.className} hasNav={!!path} mobile={bp === "mobile"}>
        {
            path && !navigationContext?.concealed && <StyledContentLayoutLeftSlot mobile>
                <IconButton onClick={() => history.replaceRaw(path)} icon={<IcoBack />} size="small" />
            </StyledContentLayoutLeftSlot> || null
        }
        {props.children}
    </StyledContentLayoutHeader>
}
export interface ContentLayoutContentsProps {
    className?: string;
    style?: CSSProperties;
    sidePanel?: React.ReactNode;
}
export const ContentLayoutContents = React.forwardRef<
    HTMLDivElement,
    React.PropsWithChildren<ContentLayoutContentsProps>
>((props, ref) => {
    const [bp] = useBreakpoint();
    if (props.sidePanel) {
        return <SplitContents>
            <StyledContentLayoutContents ref={ref} style={props.style} className={props.className} mobile={bp === "mobile"}>
                {props.children}
            </StyledContentLayoutContents>
            {props.sidePanel}
        </SplitContents>
    }
    return <StyledContentLayoutContents ref={ref} className={props.className} mobile={bp === "mobile"}>
        {props.children}
    </StyledContentLayoutContents>
});
ContentLayoutContents.displayName = ContentLayoutContents.name;

export const ContentLayout: React.FC<{ className?: string }> = props => {
    return <StyledContentLayout className={props.className}>
        {props.children}
    </StyledContentLayout>
}

const StyledContentLayoutActions = styled.div<{ mobile: boolean }>`
    padding: ${props => props.theme.spacing(1, props.mobile ? 1 : 2)};
    background-color: ${p => p.theme.palette.surface};
    border-top: solid 1px ${p => p.theme.palette.ui.border};
`

export const ContentLayoutActions: React.FC = props => {
    const [bp] = useBreakpoint();
    return <StyledContentLayoutActions mobile={bp === "mobile"}>
        {props.children}
    </StyledContentLayoutActions>
}
